import React, { Fragment, createRef, useCallback, useMemo, useRef } from "react";
import { FlatList, ScrollView, View } from "react-native";
import { Error, useScreenSize, useScrollTo } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Layout, QuestionType } from "../../../../../../../projection/question/question.constants";
import { metadataHasPresentationGridLayout, metadataHasPresentationHorizonalLayout, questionHasLayout, } from "../../../../../../../projection/question/question.typeguards";
import { Question } from "../../Question";
import { useAnswerForId } from "../../behaviours/useAnswers";
import { useQuestionValidationError } from "../../behaviours/useQuestionValidationError";
import { style as questionLayoutStyle } from "./QuestionLayout.style";
const Grid = ({ domain, country, level, question, error, tradename, segment }) => {
    const screenSize = useScreenSize();
    const numColumns = metadataHasPresentationGridLayout(question.metadata)
        ? question.metadata.layoutColumns[screenSize]
        : 2;
    const childrenQuestionHasLayout = question.children.length > 0 && questionHasLayout(question.children[0]);
    const style = useMemo(() => questionLayoutStyle(), []);
    const gridStyle = style.children;
    const renderItem = useCallback(({ item, index }) => (React.createElement(View, { style: [
            { flex: 1 / numColumns },
            (index + 1) % numColumns !== 0 && style.gridColumnGap,
            index - numColumns >= 0 && style.gridRowGap,
        ] },
        React.createElement(Question, { country: country, domain: domain, 
            // If this question does not have a translationKey (title), do not increase the level
            level: question.translationKey ? level + 1 : level, question: item, questionParentId: question.id, segment: segment, tradename: tradename }))), [
        country,
        domain,
        level,
        numColumns,
        question.id,
        question.translationKey,
        segment,
        style.gridColumnGap,
        style.gridRowGap,
        tradename,
    ]);
    return (React.createElement(FlatList, { key: numColumns, bounces: false, contentContainerStyle: [style.grid, error && style.gridError], data: question.children, keyExtractor: ({ id }) => id, numColumns: numColumns, renderItem: ({ item, index }) => renderItem({ item, index }), showsHorizontalScrollIndicator: false, showsVerticalScrollIndicator: false, style: !childrenQuestionHasLayout && gridStyle, testID: "question-layout-grid" }));
};
const HorizontalList = ({ domain, country, level, question, error, tradename, segment }) => {
    const childrenQuestionHasLayout = question.children.length > 0 && questionHasLayout(question.children[0]);
    const { answer } = useAnswerForId({ id: question.id });
    const initialAnswer = useRef(answer);
    const listRef = useRef(null);
    const itemRefs = useRef(question.children.map(() => createRef()));
    const scrollTo = useScrollTo({ scrollRef: listRef });
    const initialScrollDone = useRef(false);
    const handleInitialScroll = useCallback(() => {
        const selectedAnswerIndex = initialAnswer.current && initialAnswer.current.length > 0 && !initialScrollDone.current
            ? question.children.map((child) => child.id).indexOf(initialAnswer.current[0])
            : undefined;
        if (!selectedAnswerIndex) {
            return;
        }
        scrollTo({ ref: itemRefs.current[selectedAnswerIndex], animated: false });
        initialScrollDone.current = true;
    }, [question.children, scrollTo]);
    const style = useMemo(() => questionLayoutStyle(), []);
    return (React.createElement(ScrollView, { ref: listRef, bounces: false, overScrollMode: "never", showsHorizontalScrollIndicator: false, style: [style.horizontalList, !childrenQuestionHasLayout && style.children], testID: "question-layout-horizontal-list", horizontal: true, onContentSizeChange: handleInitialScroll },
        React.createElement(View, { style: [
                style.horizontalListContent,
                error && style.horizontalListContentError,
                error && tradename === Tradename.LOOKIERO && style.horizontalListContentErrorLookiero,
            ] }, question.children.map((item, index) => (React.createElement(View, { key: item.id, ref: itemRefs.current[index], style: [style.horizontalItem, index < question.children.length - 1 && style.horizontalGap] },
            React.createElement(Question, { country: country, domain: domain, 
                // If this question does not have a translationKey (title), do not increase the level
                level: question.translationKey ? level + 1 : level, question: item, questionParentId: question.id, segment: segment, tradename: tradename })))))));
};
const HorizontalWrap = ({ domain, country, level, question, error, tradename, segment }) => {
    const isHostRank = question.type === QuestionType.HOST_RANK;
    const style = useMemo(() => questionLayoutStyle(), []);
    const renderItem = useCallback(({ item }) => (React.createElement(View, { key: item.id, style: [style.horizontalItem, isHostRank && style.listItemHostRank, style.horizontalWrapGap] },
        React.createElement(Question, { country: country, domain: domain, 
            // If this question does not have a translationKey (title), do not increase the level
            level: question.translationKey ? level + 1 : level, question: item, questionParentId: question.id, segment: segment, tradename: tradename }))), [
        country,
        domain,
        isHostRank,
        level,
        question.id,
        question.translationKey,
        segment,
        style.horizontalItem,
        style.horizontalWrapGap,
        style.listItemHostRank,
        tradename,
    ]);
    return (React.createElement(View, { style: [style.horizontalWrap, isHostRank && style.hostRank, error && style.horizontalWrapError], testID: "question-layout-horizontal-wrap" }, question.children.map((item, index) => renderItem({ item, index }))));
};
const Horizontal = ({ domain, country, level, question, error, tradename, segment }) => {
    const screenSize = useScreenSize();
    const scrollable = metadataHasPresentationHorizonalLayout(question.metadata) && question.metadata.layoutScroll?.[screenSize];
    const Component = scrollable ? HorizontalList : HorizontalWrap;
    return (React.createElement(Component, { country: country, domain: domain, error: error, level: level, question: question, segment: segment, tradename: tradename }));
};
const Vertical = ({ domain, country, level, question, error, tradename, segment }) => {
    const style = useMemo(() => questionLayoutStyle(), []);
    const hasChildren = question.children.length > 0;
    const isCheckbox = hasChildren && question.children[0].type === QuestionType.CHECKBOX;
    const childrenQuestionHasLayout = hasChildren && questionHasLayout(question.children[0]);
    const renderItem = useCallback(({ item, index }) => (React.createElement(View, { key: item.id, style: index !== 0 && style.verticalGap },
        React.createElement(Question, { country: country, domain: domain, 
            // If this question does not have a translationKey (title), do not increase the level
            level: question.translationKey ? level + 1 : level, question: item, questionParentId: question.id, segment: segment, tradename: tradename }),
        isCheckbox && index < question.children.length - 1 && React.createElement(View, { style: [style.separator, style.verticalGap] }))), [
        country,
        domain,
        isCheckbox,
        level,
        question.children.length,
        question.id,
        question.translationKey,
        segment,
        style.separator,
        style.verticalGap,
        tradename,
    ]);
    return (React.createElement(View, { style: [!childrenQuestionHasLayout && style.children, error && style.verticalError], testID: "question-layout-vertical" }, question.children.map((item, index) => renderItem({ item, index }))));
};
const LayoutComponent = {
    [Layout.GRID]: Grid,
    [Layout.VERTICAL_LIST]: Vertical,
    [Layout.HORIZONTAL_LIST]: Horizontal,
};
const QuestionLayout = ({ domain, country, level, question, tradename, segment }) => {
    const hasLayout = questionHasLayout(question);
    const Component = hasLayout ? LayoutComponent[question.metadata.layout] : Fragment;
    const style = useMemo(() => questionLayoutStyle(), []);
    const ref = useRef(null);
    const validationError = useQuestionValidationError({
        domain,
        questionId: question.id,
        ref,
        segment,
    });
    return hasLayout ? (React.createElement(View, { ref: ref },
        React.createElement(Component, { country: country, domain: domain, error: Boolean(validationError), level: level, question: question, segment: segment, tradename: tradename }),
        validationError && React.createElement(Error, { error: validationError, level: 3, style: style.error }))) : (React.createElement(React.Fragment, null, question.children.map((childQuestion) => (React.createElement(Question, { key: childQuestion.id, country: country, domain: domain, 
        // If this question does not have a translationKey (title), do not increase the level
        level: question.translationKey ? level + 1 : level, question: childQuestion, questionParentId: question.id, segment: segment, tradename: tradename })))));
};
export { QuestionLayout };
